/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {DragDropModule} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpHandler,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {APP_INITIALIZER, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {MatCardModule} from '@angular/material/card';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatChipsModule} from '@angular/material/chips';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatSortModule} from '@angular/material/sort';
import {MatStepperModule} from '@angular/material/stepper';
import {MatTableModule} from '@angular/material/table';
import {MatTabsModule} from '@angular/material/tabs';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatTreeModule} from '@angular/material/tree';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule} from '@angular/router';
import {FabBreadcrumbsModule} from '@fabric/components/breadcrumbs';
import {APP_NAV_ITEMS} from '@fabric/components/common';
import {FabHeaderModule} from '@fabric/components/header';
import {
  FabAppInfoModule,
  FabAppLauncherModule,
  FabPopoverHeaderActionComponent,
  FabUserInfoModule
} from '@fabric/components/header-actions';
import {FabLoadingSpinnerModule} from '@fabric/components/loading-spinner';
import {FabMultiselectModule} from '@fabric/components/multiselect';
import {FabSidenavModule} from '@fabric/components/sidenav';
import {FabTableFilterModule} from '@fabric/components/table-filter';
import {FabTopnavModule} from '@fabric/components/topnav';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {AgGridAngular} from 'ag-grid-angular';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {EngTranDtlRptComponent} from './components/admin/eng-tran-dtl-rpt/eng-tran-dtl-rpt.component';
import {
  EditArchDeviancePopUpComponent
} from './components/admin/manage-arch-deviances/edit-arch-deviance-pop-up/edit-arch-deviance-pop-up.component';
import {ManageArchDeviancesComponent} from './components/admin/manage-arch-deviances/manage-arch-deviances.component';
import {
  EngineAttributesComponent
} from './components/admin/manage-attributes/engine-attributes/engine-attributes.component';
import {
  ManageTransmissionsComponent
} from './components/admin/manage-attributes/manage-transmissions/manage-transmissions.component';
import {
  UpdateAttributePopupComponent
} from './components/admin/manage-attributes/update-attribute-popup/update-attribute-popup.component';
import {
  VehicleAttributesComponent
} from './components/admin/manage-attributes/vehicle-attributes/vehicle-attributes.component';
import {ManageForecastStateComponent} from './components/admin/manage-forecast-state/manage-forecast-state.component';
import {
  EditAddGlobalScenarioComponent
} from './components/admin/manage-global-scenario/edit-add-global-scenario/edit-add-global-scenario.component';
import {
  ManageGlobalScenarioComponent
} from './components/admin/manage-global-scenario/manage-global-scenario.component';
import {
  AddMarketAreaPopUpComponent
} from './components/manage/manage-market-area-grouping/add-market-area-pop-up/add-market-area-pop-up.component';
import {
  EditMarketAreaPopUpComponent
} from './components/manage/manage-market-area-grouping/edit-market-area-pop-up/edit-market-area-pop-up.component';
import {
  ManageMarketAreaGroupingComponent
} from './components/manage/manage-market-area-grouping/manage-market-area-grouping.component';
import {
  ManageMarketingDivisionsComponent
} from './components/admin/manage-marketing-divisions/manage-marketing-divisions.component';
import {
  AddPlanningMarketPopUpComponent
} from './components/admin/manage-planning-market/add-planning-market-pop-up/add-planning-market-pop-up.component';
import {
  EditPlanningMarketPopUpComponent
} from './components/admin/manage-planning-market/edit-planning-market-pop-up/edit-planning-market-pop-up.component';
import {
  ManagePlanningMarketComponent
} from './components/admin/manage-planning-market/manage-planning-market.component';
import {
  ManagePropulsionTypesComponent
} from './components/admin/manage-propulsion-types/manage-propulsion-types.component';
import {ManageSalesLagComponent} from './components/admin/manage-sales-lag/manage-sales-lag.component';
import {
  ManageScenarioCategoryListComponent
} from './components/admin/manage-scenario-category-list/manage-scenario-category-list.component';
import {
  ManageScenarioTypeListComponent
} from './components/admin/manage-scenario-type-list/manage-scenario-type-list.component';
import {ManageScenariosComponent} from './components/admin/manage-scenarios/manage-scenarios.component';
import {
  AddSubregionPopUpComponent
} from './components/admin/manage-subregions/add-subregion-pop-up/add-subregion-pop-up.component';
import {ManageSubregionsComponent} from './components/admin/manage-subregions/manage-subregions.component';
import {
  PropulsionDuplicateCheckComponent
} from './components/admin/propulsion-duplicate-check/propulsion-duplicate-check.component';
import {
  PropulsionPortfolioLoadComponent
} from './components/admin/propulsion-portfolio-load/propulsion-portfolio-load.component';
import {PropulsionSnapshotComponent} from './components/admin/propulsion-snapshot/propulsion-snapshot.component';
import {SegmentReportComponent} from './components/admin/segment-report/segment-report/segment-report.component';
import {
  AddSegmentPopupComponent
} from './components/admin/segment-scheme-management/add-segment-popup/add-segment-popup.component';
import {
  SegmentSchemeManagementComponent
} from './components/admin/segment-scheme-management/segment-scheme-management.component';
import {
  SegmentSuffixManagementComponent
} from './components/admin/segment-suffix-management/segment-suffix-management.component';
import {
  RemoveObsoleteRecordsComponent
} from './components/admin/shared-remove-obsolete-records/remove-obsolete-records/remove-obsolete-records.component';
import {
  SharedRemoveObsoleteRecordsComponent
} from './components/admin/shared-remove-obsolete-records/shared-remove-obsolete-records.component';
import {AddUserComponent} from './components/admin/user-maintenance/add-user/add-user.component';
import {EditUserComponent} from './components/admin/user-maintenance/edit-user/edit-user.component';
import {UserMaintenanceComponent} from './components/admin/user-maintenance/user-maintenance.component';
import {
  ViewUserMapPopupComponent
} from './components/admin/user-maintenance/view-user-map-popup/view-user-map-popup.component';
import {
  VehicleDuplicateCheckComponent
} from './components/admin/vehicle-duplicate-check/vehicle-duplicate-check.component';
import {
  VehiclePortfolioArchiveComponent
} from './components/admin/vehicle-portfolio-archive/vehicle-portfolio-archive.component';
import {
  VehiclePortfolioLoadComponent
} from './components/admin/vehicle-portfolio-load/vehicle-portfolio-load.component';
import {
  VehiclePortfolioTransferComponent
} from './components/admin/vehicle-portfolio-transfer/vehicle-portfolio-transfer.component';
import {VehicleSnapshotComponent} from './components/admin/vehicle-snapshot/vehicle-snapshot.component';
import {HomeComponent} from './components/home/home.component';
import {
  CreateIndustryScenarioComponent
} from './components/industry-scenario/create-industry-scenario/create-industry-scenario.component';
import {DragDirective} from './components/industry-scenario/create-industry-scenario/dragDrop.directive';
import {
  IndustryQuerySaveComponent
} from './components/industry-scenario/forecast-template-save/industry-query-save.component';
import {
  QueryIndustryPopUpComponent
} from './components/industry-scenario/query-industry-pop-up/query-industry-pop-up.component';
import {
  BulkPropScenUpdatePopUpComponent
} from "./components/manage/bulk-update-to-portfolio/bulk-prop-scen-update-pop-up/bulk-prop-scen-update-pop-up.component";
import {
  BulkUpdateToPortfolioComponent
} from "./components/manage/bulk-update-to-portfolio/bulk-update-to-portfolio.component";
import {ManageBrandFamilyComponent} from './components/manage/manage-brand-family/manage-brand-family.component';
import {
  GlobalSegmentValidatorDirective
} from './components/manage/manage-brand-family/validators/invalid-global-segment.directive';
import {
  InvalidSegmentValidatorDirective
} from './components/manage/manage-brand-family/validators/invalid-segment.directive';
import {
  EditIndustryScenarioPopupComponent
} from './components/manage/manage-scenario-details/industry-scenario/edit-industry-scenario-popup/edit-industry-scenario-popup.component';
import {
  ManageIndustryScenarioComponent
} from './components/manage/manage-scenario-details/industry-scenario/manage-industry-scenario.component';
import {
  EditPropulsionScenarioPopupComponent
} from './components/manage/manage-scenario-details/propulsion-scenario/edit-propulsion-scenario-popup/edit-propulsion-scenario-popup.component';
import {
  PropulsionScenarioComponent
} from './components/manage/manage-scenario-details/propulsion-scenario/propulsion-scenario.component';
import {
  EditVehicleScenarioPopupComponent
} from './components/manage/manage-scenario-details/vehicle-scenario/edit-vehicle-scenario-popup/edit-vehicle-scenario-popup.component';
import {
  ManageVehicleScenarioComponent
} from './components/manage/manage-scenario-details/vehicle-scenario/manage-vehicle-scenario.component';
import {
  CreatePropulsionForecastTemplateComponent
} from './components/propulsion-scenario/create-propulsion-forecast-template/create-propulsion-forecast-template.component';
import {
  CreatePropulsionScenarioComponent
} from './components/propulsion-scenario/create-scenario/create-propulsion-scenario.component';
import {
  DeletePropDetailComponent
} from './components/propulsion-scenario/delete-prop-detail/delete-prop-detail.component';
import {
  PropulsionTemplateSaveComponent
} from './components/propulsion-scenario/forecast-template-save/propulsion-template-save.component';
import {
  ImportGlobalPropulsionForecastComponent
} from './components/propulsion-scenario/import-global-propulsion-forecast/import-global-propulsion-forecast.component';
import {
  UpdatePropulsionScenarioWithExtDataComponent
} from './components/propulsion-scenario/update-propulsion-scenario-with-ext-data/update-propulsion-scenario-with-ext-data.component';
import {
  UpdatePropulsionScenarioWithScenarioComponent
} from './components/propulsion-scenario/update-propulsion-scenario-with-scenario/update-propulsion-scenario-with-scenario.component';
import {ComparePropVolRptComponent} from './components/reports/compare-prop-vol-rpt/compare-prop-vol-rpt.component';
import {
  GlobalPropulsionForecastTemplateComponent
} from "./components/reports/global-propulsion-forecast-template/global-propulsion-forecast-template";
import {
  GlobalScenarioAssociationsComponent
} from './components/reports/global-scenario-associations/global-scenario-associations.component';
import {
  GlobalSegShrAuditRptComponent
} from "./components/reports/global-seg-shr-audit-rpt/global-seg-shr-audit-rpt.component";
import {
  GlobalVehicleForecastTemplateComponent
} from './components/reports/global-vehicle-forecast-template/global-vehicle-forecast-template.component';
import {
  IndustryConsolidationReportComponent
} from "./components/reports/industry-consolidation-report/industry-consolidation-report.component";
import {
  MarketShareSgmntRptComponent
} from './components/reports/market-share-sgmnt-rpt/market-share-sgmnt-rpt.component';
import {
  PivotTableDataPropulsionComponent
} from './components/reports/pivot-table-data-propulsion/pivot-table-data-propulsion.component';
import {
  PivotTableDataVehicleComponent
} from './components/reports/pivot-table-data-vehicle/pivot-table-data-vehicle.component';
import {
  PortfolioArchiveReportComponent
} from './components/reports/portfolio-reports/portfolio-archive-report/portfolio-archive-report.component';
import {PortfolioReportsComponent} from './components/reports/portfolio-reports/portfolio-reports.component';
import {
  PortfolioVarianceRptComponent
} from './components/reports/portfolio-reports/portfolio-variance-rpt/portfolio-variance-rpt.component';
import {
  PropulsionPortfolioReportComponent
} from './components/reports/portfolio-reports/propulsion-portfolio-report/propulsion-portfolio-report.component';
import {
  VehiclePortfolioReportComponent
} from './components/reports/portfolio-reports/vehicle-portfolio-report/vehicle-portfolio-report.component';
import {
  PropulsionConsolidationReportComponent
} from "./components/reports/propulsion-consolidation-report/propulsion-consolidation-report.component";
import {
  PrgmCodeByFuelTypeShareRptComponent
} from './components/reports/prgm-code-by-fuel-type-share-rpt/prgm-code-by-fuel-type-share-rpt.component';
import {
  RunRatesDatesReportComponent
} from './components/reports/run-rates-dates-report/run-rates-dates-report.component';
import {
  VarianceMktDivSgmntRptComponent
} from './components/reports/variance-mkt-div-sgmnt-rpt/variance-mkt-div-sgmnt-rpt.component';
import {VehPropVolCompareComponent} from './components/reports/veh-prop-vol-compare/veh-prop-vol-compare.component';
import {VehVolAnalysisRptComponent} from './components/reports/veh-vol-analysis-rpt/veh-vol-analysis-rpt.component';
import {
  VehicleConsolidationReportComponent
} from './components/reports/vehicle-consolidation-report/vehicle-consolidation-report.component';
import {
  AttachPropulsionScenarioPopUpComponent
} from './components/vehicle-scenario/attach-propulsion-scenario-pop-up/attach-propulsion-scenario-pop-up.component';
import {
  CreateVehicleScenarioComponent
} from './components/vehicle-scenario/create-vehicle-scenario/create-vehicle-scenario.component';
import {
  SelectVehicleProgramPopUpComponent
} from './components/vehicle-scenario/create-vehicle-scenario/select-vehicle-program-pop-up/select-vehicle-program-pop-up.component';
import {DeleteVehDetailComponent} from './components/vehicle-scenario/delete-veh-detail/delete-veh-detail.component';
import {QueryVehicleComponent} from "./components/vehicle-scenario/query-vehicle/query-vehicle.component";
import {
  VehicleForecastQuickEditComponent
} from './components/vehicle-scenario/query-vehicle/vehicle-forecast-quick-edit/vehicle-forecast-quick-edit.component';
import {
  SaveGlobalVehicleTemplateComponent
} from './components/vehicle-scenario/save-global-vehicle-template/save-global-vehicle-template.component';
import {
  UpdatePropulsionScenarioWithPortComponent
} from './components/vehicle-scenario/update-propulsion-scenario-with-port/update-propulsion-scenario-with-port.component';
import {
  UpdateVehicleScenarioWithExtDataComponent
} from './components/vehicle-scenario/update-vehicle-scenario-with-ext-data/update-vehicle-scenario-with-ext-data.component';
import {
  UpdateVehicleScenarioWithScenarioComponent
} from './components/vehicle-scenario/update-vehicle-scenario-with-scenario/update-vehicle-scenario-with-scenario.component';
import {
  VehicleForecastTemplateSaveComponent
} from "./components/vehicle-scenario/vehicle-forecast-template-save/vehicle-forecast-template-save.component";
import {AuthenticationModule} from './core/authentication/authentication.module';
import {AuthErrorComponent} from './core/components/auth-error/auth-error.component';
import {CustomButtonCellComponent} from './core/components/custom-button-cell/custom-button-cell.component';
import {ExportButtonComponent} from './core/components/export-button/export-button.component';
import {FileUploadButtonComponent} from './core/components/file-upload-button/file-upload-button.component';
import {
  GlobalScenarioSelectPopUpComponent
} from './core/components/global-scenario-select/global-scenario-select-pop-up/global-scenario-select-pop-up.component';
import {GlobalScenarioSelectComponent} from './core/components/global-scenario-select/global-scenario-select.component';
import {
  IndustryScenarioSelectPopUpComponent
} from './core/components/industry-scenario-select/industry-scenario-select-pop-up/industry-scenario-select-pop-up.component';
import {
  IndustryScenarioSelectComponent
} from './core/components/industry-scenario-select/industry-scenario-select.component';
import {AddPopUpComponent} from './core/components/pop-ups/add-pop-up/add-pop-up.component';
import {DeletePopUpComponent} from './core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {InfoPopUpComponent} from './core/components/pop-ups/info-pop-up/info-pop-up.component';
import {SelectorPopUpComponent} from "./core/components/pop-ups/selector-pop-up/selector-pop-up.component";
import {
  TableDisplayConfirmationPopUpComponent
} from './core/components/pop-ups/table-display-confirmation-pop-up/table-display-confirmation-pop-up.component';
import {
  TableDisplayPopUpComponent
} from './core/components/pop-ups/table-display-pop-up/table-display-pop-up.component';
import {UpdatePopUpComponent} from './core/components/pop-ups/update-pop-up/update-pop-up.component';
import {
  PropulsionScenarioSelectComponent
} from './core/components/propulsion-scenario-select/propulsion-scenario-select.component';
import {SheetSelectorComponent} from './core/components/sheet-selector/sheet-selector.component';
import {
  UpdateScenarioWithSegmentSchemeComponent
} from './core/components/update-scenario-with-segment-scheme/update-scenario-with-segment-scheme.component';
import {UserPreferenceComponent} from './core/components/user-preference/user-preference.component';
import {
  VehicleScenarioSelectPopUpComponent
} from './core/components/vehicle-scenario-select/vehicle-scenario-select-pop-up/vehicle-scenario-select-pop-up.component';
import {
  VehicleScenarioSelectComponent
} from './core/components/vehicle-scenario-select/vehicle-scenario-select.component';
import {appNavItems} from './core/config/app-nav-items';
import {AutocompleteFilterComponent} from './core/filters/autocomplete-filter/autocomplete-filter.component';
import {ConfigService} from './services/config.service';
import {ExceptionInterceptService} from './services/exception-intercept.service';

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '../assets/languages/', '.json');
}

/**
 * Function that is called once when the app is initially started
 */
//eslint-disable-next-line
function appInitializer(configService: ConfigService, handler: HttpHandler): Function {
  return () => {
    return configService.initialize(handler);
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    AuthErrorComponent,
    SegmentSchemeManagementComponent,
    AddSegmentPopupComponent,
    UpdateAttributePopupComponent,
    VehicleAttributesComponent,
    EngineAttributesComponent,
    ManageTransmissionsComponent,
    ManageScenarioCategoryListComponent,
    ManageMarketingDivisionsComponent,
    ManageBrandFamilyComponent,
    ManageSalesLagComponent,
    GlobalSegmentValidatorDirective,
    InvalidSegmentValidatorDirective,
    VehicleSnapshotComponent,
    SharedRemoveObsoleteRecordsComponent,
    UserMaintenanceComponent,
    AddUserComponent,
    EditUserComponent,
    ViewUserMapPopupComponent,
    ManageScenarioTypeListComponent,
    AddPopUpComponent,
    DeletePopUpComponent,
    UpdatePopUpComponent,
    ManagePropulsionTypesComponent,
    ManageForecastStateComponent,
    SharedRemoveObsoleteRecordsComponent,
    PropulsionPortfolioLoadComponent,
    FileUploadButtonComponent,
    VehiclePortfolioLoadComponent,
    SheetSelectorComponent,
    ExportButtonComponent,
    SegmentReportComponent,
    RemoveObsoleteRecordsComponent,
    PropulsionSnapshotComponent,
    ManagePlanningMarketComponent,
    EditPlanningMarketPopUpComponent,
    ManageIndustryScenarioComponent,
    EditIndustryScenarioPopupComponent,
    ManageVehicleScenarioComponent,
    CreateIndustryScenarioComponent,
    EditIndustryScenarioPopupComponent,
    ManageVehicleScenarioComponent,
    EditVehicleScenarioPopupComponent,
    PropulsionScenarioComponent,
    EditPropulsionScenarioPopupComponent,
    ManageMarketAreaGroupingComponent,
    AddMarketAreaPopUpComponent,
    EditMarketAreaPopUpComponent,
    ManageGlobalScenarioComponent,
    ManageArchDeviancesComponent,
    EditArchDeviancePopUpComponent,
    VehicleScenarioSelectPopUpComponent,
    EditAddGlobalScenarioComponent,
    ManageScenariosComponent,
    VehicleDuplicateCheckComponent,
    PropulsionDuplicateCheckComponent,
    EditAddGlobalScenarioComponent,
    DragDirective,
    EditAddGlobalScenarioComponent,
    AttachPropulsionScenarioPopUpComponent,
    CreateVehicleScenarioComponent,
    InfoPopUpComponent,
    SelectVehicleProgramPopUpComponent,
    MarketShareSgmntRptComponent,
    UpdateScenarioWithSegmentSchemeComponent,
    GlobalScenarioSelectPopUpComponent,
    SegmentSuffixManagementComponent,
    UpdatePropulsionScenarioWithPortComponent,
    QueryIndustryPopUpComponent,
    QueryVehicleComponent,
    VehicleForecastTemplateSaveComponent,
    IndustryQuerySaveComponent,
    GlobalPropulsionForecastTemplateComponent,
    VehicleConsolidationReportComponent,
    GlobalSegShrAuditRptComponent,
    IndustryConsolidationReportComponent,
    PropulsionConsolidationReportComponent,
    PrgmCodeByFuelTypeShareRptComponent,
    RunRatesDatesReportComponent,
    BulkUpdateToPortfolioComponent,
    VehicleScenarioSelectComponent,
    GlobalScenarioSelectComponent,
    SelectorPopUpComponent,
    BulkPropScenUpdatePopUpComponent,
    VehPropVolCompareComponent,
    VehVolAnalysisRptComponent,
    CreatePropulsionForecastTemplateComponent,
    PropulsionTemplateSaveComponent,
    PivotTableDataPropulsionComponent,
    CreatePropulsionScenarioComponent,
    PropulsionScenarioSelectComponent,
    PivotTableDataVehicleComponent,
    VarianceMktDivSgmntRptComponent,
    GlobalScenarioAssociationsComponent,
    ImportGlobalPropulsionForecastComponent,
    PropulsionPortfolioReportComponent,
    VehiclePortfolioReportComponent,
    PortfolioReportsComponent,
    PortfolioVarianceRptComponent,
    UpdateVehicleScenarioWithScenarioComponent,
    TableDisplayPopUpComponent,
    UpdateVehicleScenarioWithExtDataComponent,
    AutocompleteFilterComponent,
    VehicleForecastQuickEditComponent,
    DeletePropDetailComponent,
    DeleteVehDetailComponent,
    CustomButtonCellComponent,
    GlobalVehicleForecastTemplateComponent,
    SaveGlobalVehicleTemplateComponent,
    IndustryScenarioSelectComponent,
    IndustryScenarioSelectPopUpComponent,
    ComparePropVolRptComponent,
    VehiclePortfolioTransferComponent,
    VehiclePortfolioArchiveComponent,
    PortfolioArchiveReportComponent,
    AddPlanningMarketPopUpComponent,
    ManageSubregionsComponent,
    AddSubregionPopUpComponent,
    UpdatePropulsionScenarioWithScenarioComponent,
    TableDisplayConfirmationPopUpComponent,
    UpdatePropulsionScenarioWithExtDataComponent,
    EngTranDtlRptComponent,
    UserPreferenceComponent
  ],
  bootstrap: [AppComponent], imports: [AuthenticationModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FabBreadcrumbsModule,
    FabAppInfoModule,
    FabAppLauncherModule,
    FabHeaderModule,
    FabSidenavModule,
    FabUserInfoModule,
    FabLoadingSpinnerModule,
    MatSnackBarModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    AppRoutingModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    FormsModule,
    MatDialogModule,
    RouterModule,
    MatSortModule,
    MatInputModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatTabsModule,
    MatCheckboxModule,
    MatStepperModule,
    MatSelectModule,
    MatTreeModule,
    MatMenuModule,
    MatChipsModule,
    MatRadioModule,
    MatSlideToggleModule,
    FabTopnavModule,
    MatPaginatorModule,
    MatListModule,
    MatProgressBarModule,
    FabTableFilterModule,
    MatAutocompleteModule,
    MatListModule,
    FabMultiselectModule,
    DragDropModule,
    MatListModule,
    FabPopoverHeaderActionComponent,
    MatButtonToggle,
    MatButtonToggleGroup, AgGridAngular], providers: [
    {provide: APP_NAV_ITEMS, useValue: appNavItems},
    {provide: APP_INITIALIZER, deps: [ConfigService, HttpHandler], multi: true, useFactory: appInitializer},
    {provide: MatDialogRef, useValue: {}},
    {provide: MAT_DIALOG_DATA, useValue: {}},
    {provide: HTTP_INTERCEPTORS, useClass: ExceptionInterceptService, multi: true},
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
}
